import { Button, ButtonVariant } from "@src/components/button/Button";
import Checkbox from "@src/components/vGrid/Checkbox";
import { Dropdown, IItems } from "@src/components/dropdown/Dropdown";
import { Switch } from "@src/components/switch/Switch";
import { DateFormatsEnum, formatDate } from "@src/components/utils/dateUtils";
import { OptionType } from "@src/components/utils/reactSelectUtils";
import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import { EventRecord } from "@src/store/pricing/queryEventsSlice";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./InvoiceForm.module.scss";
import { purchasingActions, salesActions } from "@src/store";
import { refreshPricingInventory } from "@src/store/pricing/refreshPricingInventory";
import { formatCurrency } from "@src/components/utils/numberUtils";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { clx } from "@src/components/utils/stringUtils";
import { toast } from "sonner";

export interface IInvoiceFormProps {
	item?: EventInventoryRecord;
	event?: EventRecord | null;
	availableSeats: Array<OptionType<number>>;
	onClose?: () => void;
	onSuccess?: () => void;
}

type TDeliveryType = "None" | "Email" | "Physical";
const DELIVERY_TYPES: Array<TDeliveryType> = ["None", "Email", "Physical"];
const DEFAULT_EXCHANGE_NAME = "External Invoice";
const CANCEL_REFUND = "Cancelled/Refund";
const STOP_LOSS = "Stop Loss";
const CUSTOMER_NAME_MAX_LENGTH = 100;
const SHIPPING_LABEL_MIN_BYTES = 2048;
const SHIPPING_LABEL_MAX_BYTES = 5 * 1024 * 1024;

type TPageState = {
	deliveryType: TDeliveryType;
	salePrice: string;
	zeroOutBalance: boolean;
	selectedSeats: Array<number>;
	exchangeName: string;
	customerName: string;
	email: string;
	shippingLabel: File | null;
};

const initialPageState: TPageState = {
	deliveryType: "None",
	salePrice: "",
	zeroOutBalance: false,
	selectedSeats: [],
	exchangeName: "",
	customerName: "",
	email: "",
	shippingLabel: null,
};

export const InvoiceForm: React.FC<IInvoiceFormProps> = ({ item, event, availableSeats, onClose, onSuccess }) => {
	const dispatch = useAppDispatch();

	const exchanges = useAppSelector((state) => state.sales.exchanges);
	const companyName = useAppSelector((state) => state.settings.companyName);
	const loading = useAppSelector((state) => state.sales.createExternalInvoice.loading);

	const [pageState, setPageState] = useState<TPageState>(initialPageState);
	const shippingLabelInputRef = useRef<HTMLInputElement>(null);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const marketplaceOptions = useMemo(() => exchanges.data.filter((x) => x.Show_On_Invoice), [exchanges.version]);
	const selectedExchange = marketplaceOptions.find((x) => x.Exchange_Name === pageState.exchangeName);
	const marketplaceItems: Array<IItems> = marketplaceOptions.map((x) => ({
		id: x.Customer_ID ?? 0,
		value: x.Exchange_Name,
		label: x.Exchange_Name,
	}));

	useEffect(() => {
		if (!exchanges.init) dispatch(salesActions.listExchanges({}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (pageState.exchangeName || !marketplaceOptions.length) return;
		const def = marketplaceOptions.find((x) => x.Exchange_Name === DEFAULT_EXCHANGE_NAME) ?? marketplaceOptions[0];
		setPageState((prev) => ({ ...prev, exchangeName: def.Exchange_Name }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [marketplaceOptions]);

	const isCancelRefund = pageState.exchangeName === CANCEL_REFUND;
	const isStopLoss = pageState.exchangeName === STOP_LOSS;
	const isLossMarketplace = isCancelRefund || isStopLoss;

	const deliveryDisabled = isLossMarketplace;
	const showEmailFields = !deliveryDisabled && pageState.deliveryType === "Email";

	const zeroOutChecked = isCancelRefund ? true : isStopLoss ? false : pageState.zeroOutBalance;
	const zeroOutDisabled = isLossMarketplace;
	const priceLocked = zeroOutChecked || isLossMarketplace;

	const quantity = pageState.selectedSeats.length;
	const parsedSalePrice = parseFloat(pageState.salePrice);
	const isPriceValid = priceLocked || (pageState.salePrice.trim() !== "" && !isNaN(parsedSalePrice) && parsedSalePrice >= 0);
	const salePriceTotal = priceLocked ? 0 : isPriceValid ? parsedSalePrice : 0;
	const salePriceDisplay = priceLocked ? "0" : pageState.salePrice;
	const pricePerTicket = quantity > 0 ? salePriceTotal / quantity : 0;

	const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pageState.email.trim());
	const areEmailFieldsValid = !showEmailFields || isEmailValid;
	const isEmailInvalid = showEmailFields && pageState.email.trim() !== "" && !isEmailValid;

	const showShippingLabel = !deliveryDisabled && pageState.deliveryType === "Physical";
	const shippingLabelError = !pageState.shippingLabel
		? null
		: pageState.shippingLabel.type !== "image/png" && !/\.png$/i.test(pageState.shippingLabel.name)
			? "Only PNG image files are allowed"
			: pageState.shippingLabel.size < SHIPPING_LABEL_MIN_BYTES
				? "The shipping label file is too small"
				: pageState.shippingLabel.size > SHIPPING_LABEL_MAX_BYTES
					? "The shipping label file must be 5 MB or smaller"
					: null;
	const isShippingLabelValid = !showShippingLabel || (!!pageState.shippingLabel && !shippingLabelError);

	const canSubmit =
		!!item && quantity > 0 && !!selectedExchange && isPriceValid && areEmailFieldsValid && isShippingLabelValid && !loading;

	const toggleSeat = (seatValue: number) => {
		setPageState((prev) => ({
			...prev,
			selectedSeats: prev.selectedSeats.includes(seatValue)
				? prev.selectedSeats.filter((s) => s !== seatValue)
				: [...prev.selectedSeats, seatValue],
		}));
	};

	const handleSalePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPageState((prev) => ({ ...prev, salePrice: e.target.value }));
	};

	const handleToggleZeroOut = (checked: boolean) => {
		setPageState((prev) => ({ ...prev, zeroOutBalance: checked }));
	};

	const handleShippingLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0] ?? null;
		e.target.value = "";
		setPageState((prev) => ({ ...prev, shippingLabel: file }));
	};

	const closeAndReset = () => {
		setPageState(initialPageState);
		onClose?.();
	};

	const handleSubmit = async () => {
		if (!canSubmit || !item || !selectedExchange) return;
		const deliveryType = deliveryDisabled ? "None" : pageState.deliveryType;
		try {
			await dispatch(
				salesActions.createExternalInvoice({
					seatFrom: String(Math.min(...pageState.selectedSeats)),
					quantity: String(quantity),
					pricePerTicket: String(pricePerTicket),
					returnPlainText: "true",
					listingId: String(item.Listing_ID),
					companyName: item.Company_Name || companyName,
					deliveryType,
					customerName: pageState.customerName.trim(),
					email: showEmailFields ? pageState.email.trim() : "",
					SalePrice: String(salePriceTotal),
					marketplace: `${selectedExchange.Customer_ID ?? 0},${selectedExchange.Exchange_Name}`,
					...(showShippingLabel && pageState.shippingLabel ? { shippingLabel: pageState.shippingLabel } : {}),
					skipCache: true,
				}),
			).unwrap();
			dispatch(refreshPricingInventory());
			dispatch(purchasingActions.getListingsItems({ ListingIds: [item.Listing_ID], skipCache: true }));
			toast.success("Invoice created.");
			onSuccess?.();
			closeAndReset();
		} catch (error) {
			toast.error(typeof error === "string" && error ? error : "Failed to create invoice.");
		}
	};

	return (
		<div className={styles.root}>
			<div className={styles.body}>
				<div className={styles.leftCol}>
					<div className={styles.colTitle}>Selected Listing</div>
					<div className={styles.infoBlock}>
						<div className={styles.infoLabel}>Event</div>
						<div className={styles.infoValue}>{event?.Event_Name ?? "—"}</div>
					</div>
					<div className={styles.infoBlock}>
						<div className={styles.infoLabel}>Venue</div>
						<div className={styles.infoValue}>{event?.Venue_Name ?? "—"}</div>
					</div>
					<div className={styles.infoBlock}>
						<div className={styles.infoLabel}>Date</div>
						<div className={styles.infoValue}>
							{event?.Event_Date ? formatDate(event.Event_Date, DateFormatsEnum["MM/DD/YYYY"]) : "—"}
						</div>
					</div>
					<div className={styles.infoBlock}>
						<div className={styles.infoLabel}>Time</div>
						<div className={styles.infoValue}>
							{event?.Event_Date ? formatDate(event.Event_Date, DateFormatsEnum["HH:MM AM/PM"]) : "—"}
						</div>
					</div>
					<div className={styles.infoBlock}>
						<div className={styles.infoLabel}>Section</div>
						<div className={styles.infoValue}>{item?.Section ?? "—"}</div>
					</div>
					<div className={styles.infoBlock}>
						<div className={styles.infoLabel}>Row</div>
						<div className={styles.infoValue}>{item?.Row ?? "—"}</div>
					</div>
				</div>

				<div className={styles.midCol}>
					<div className={styles.colTitle}>Properties</div>

					<div className={styles.field}>
						<div className={styles.fieldLabel}>Delivery Type</div>
						<div className={styles.radioGroup}>
							{DELIVERY_TYPES.map((dt) => (
								<label
									key={dt}
									className={clx(
										styles.radioOption,
										!deliveryDisabled && pageState.deliveryType === dt ? styles.radioOptionActive : "",
									)}>
									<input
										type="radio"
										name="deliveryType"
										disabled={deliveryDisabled}
										checked={!deliveryDisabled && pageState.deliveryType === dt}
										onChange={() => setPageState((prev) => ({ ...prev, deliveryType: dt }))}
									/>
									<span>{dt}</span>
								</label>
							))}
						</div>
					</div>

					{showShippingLabel ? (
						<div className={styles.field}>
							<div className={styles.fieldLabel}>Shipping Label</div>
							<div className={styles.inputRow}>
								<input
									className={clx(styles.input, shippingLabelError ? styles.invalid : "")}
									type="text"
									readOnly
									placeholder="Upload a PNG shipping label"
									value={pageState.shippingLabel?.name ?? ""}
									onClick={() => shippingLabelInputRef.current?.click()}
								/>
								<Button variant={ButtonVariant.Secondary} onClick={() => shippingLabelInputRef.current?.click()}>
									Browse...
								</Button>
								<input
									ref={shippingLabelInputRef}
									className={styles.hiddenFileInput}
									type="file"
									accept="image/png"
									aria-label="Shipping label file"
									onChange={handleShippingLabelChange}
								/>
							</div>
							{shippingLabelError ? <div className={styles.fieldError}>{shippingLabelError}</div> : null}
						</div>
					) : null}

					<div className={styles.twoCol}>
						<div className={styles.field}>
							<div className={styles.fieldLabel}>Customer Name</div>
							<input
								className={styles.input}
								type="text"
								placeholder="Customer name"
								maxLength={CUSTOMER_NAME_MAX_LENGTH}
								value={pageState.customerName}
								onChange={(e) => setPageState((prev) => ({ ...prev, customerName: e.target.value }))}
							/>
						</div>
						{showEmailFields ? (
							<div className={styles.field}>
								<div className={styles.fieldLabel}>Email</div>
								<input
									className={clx(styles.input, isEmailInvalid ? styles.invalid : "")}
									type="email"
									placeholder="Email"
									value={pageState.email}
									onChange={(e) => setPageState((prev) => ({ ...prev, email: e.target.value }))}
								/>
								{isEmailInvalid ? (
									<div className={styles.fieldError}>A valid email is required for email delivery</div>
								) : null}
							</div>
						) : null}
					</div>

					<div className={styles.field}>
						<div className={styles.fieldLabel}>Sale Price</div>
						<div className={styles.inputRow}>
							<div className={styles.currencyPrefix}>$</div>
							<input
								className={clx(styles.input, salePriceDisplay && !isPriceValid ? styles.invalid : "")}
								type="number"
								inputMode="decimal"
								min={0}
								step="1"
								placeholder="0.00"
								value={salePriceDisplay}
								onChange={handleSalePriceChange}
								disabled={priceLocked}
							/>
						</div>
					</div>

					<Switch
						checked={zeroOutChecked}
						onChange={handleToggleZeroOut}
						label="Zero out Balance"
						isDisabled={zeroOutDisabled}
					/>

					<div className={styles.field}>
						<div className={styles.fieldLabel}>Marketplace</div>
						<Dropdown
							className={styles.marketplaceDropdown}
							theme="secondary"
							containerWidth="100%"
							buttonText={pageState.exchangeName || DEFAULT_EXCHANGE_NAME}
							items={marketplaceItems}
							onItemSelect={(mp) => setPageState((prev) => ({ ...prev, exchangeName: String(mp.value) }))}
						/>
					</div>

					<div className={styles.calcRow}>
						<div className={styles.calcChip}>
							<div className={styles.calcLabel}>Quantity</div>
							<div className={styles.calcValue}>{quantity}</div>
						</div>
						<div className={clx(styles.calcChip, styles.calcChipAccent)}>
							<div className={styles.calcLabel}>Price Per Ticket</div>
							<div className={styles.calcValue}>{formatCurrency(pricePerTicket)}</div>
						</div>
					</div>
				</div>

				<div className={styles.rightCol}>
					<div className={styles.colTitle}>Seats</div>
					{availableSeats.length ? (
						<div className={styles.seatList}>
							{availableSeats.map((seat, index) => (
								<label key={`${seat.value}-${index}`} className={styles.seatRow}>
									<Checkbox
										controlSize="sm"
										isChecked={pageState.selectedSeats.includes(seat.value)}
										onChange={() => toggleSeat(seat.value)}
									/>
									<span>{seat.label}</span>
								</label>
							))}
						</div>
					) : (
						<div className={styles.emptySeats}>No seats available.</div>
					)}
				</div>
			</div>

			<div className={styles.buttons}>
				<Button onClick={closeAndReset} variant={ButtonVariant.Secondary} disabled={loading}>
					Cancel
				</Button>
				<Button onClick={handleSubmit} variant={ButtonVariant.Primary} isLoading={loading} disabled={!canSubmit}>
					Submit
				</Button>
			</div>
		</div>
	);
};

export default InvoiceForm;
